import axios, { AxiosRequestConfig } from 'axios';

import { API_BASE_URL as baseUrl } from 'src/utils/api-url';

export interface AuthModel {
  accessToken: string;
  refreshToken: string;
}

interface AuthRequest {
  username: string;
  password: string;
}

export type TwoFactorMethod = 'totp' | 'emailOtp';

/**
 * What a correct password answers when the service requires a second factor (XBIT-4742). The token is
 * kept in React state only: it is a credential for the /auth/2fa/* routes.
 */
export interface LoginChallenge {
  twoFactorRequired: true;
  challengeToken: string;
  expiresIn: number;
  step: 'verify' | 'enroll';
  /** The factors to run, in order (XBIT-4796): `emailOtp` first when present, then `totp`. */
  methods: TwoFactorMethod[];
  /** How the first factor was proven; absent on a password answer from an older service. */
  origin?: 'password' | 'google';
}

/** The answer of an email-code verify that leaves the authenticator code due (XBIT-4796). */
export type TwoFactorNext = { twoFactorRequired: true; next: 'totp' };

export function isTwoFactorNext(value: AuthModel | TwoFactorNext): value is TwoFactorNext {
  return (value as TwoFactorNext).twoFactorRequired === true && (value as TwoFactorNext).next === 'totp';
}

/** A service older than XBIT-4742 answers the token pair straight away. */
export function isLoginChallenge(value: AuthModel | LoginChallenge): value is LoginChallenge {
  return (value as LoginChallenge).twoFactorRequired === true && typeof (value as LoginChallenge).challengeToken === 'string';
}

export async function login(params: AuthRequest): Promise<AuthModel | LoginChallenge> {
  const { username='', password='' } = params;

  const url = `${baseUrl}/auth/login`;
  const resp = await axios.post<AuthModel | LoginChallenge>(url, { identifier: username, password });
  return resp.data;
}

export interface EmailOtpSent {
  sentTo: string;
  expiresIn: number;
  resendAfter: number;
}

// The /auth/2fa/* routes are public: the challenge token in the body is the credential, so no
// Authorization header is sent.

export async function sendEmailOtp(challengeToken: string): Promise<EmailOtpSent> {
  const resp = await axios.post<EmailOtpSent>(`${baseUrl}/auth/2fa/email-otp/send`, { challengeToken });
  return resp.data;
}

export async function verifyTwoFactor(
  challengeToken: string,
  method: TwoFactorMethod,
  otp: string
): Promise<AuthModel | TwoFactorNext> {
  const resp = await axios.post<AuthModel | TwoFactorNext>(`${baseUrl}/auth/2fa/verify`, {
    challengeToken,
    method,
    otp,
  });
  return resp.data;
}

/** Proves the email on file with the emailed code; answers the otpauth URI once. */
export async function startTotpEnrollment(
  challengeToken: string,
  otp: string
): Promise<{ otpauthUri: string }> {
  const resp = await axios.post<{ otpauthUri: string }>(`${baseUrl}/auth/2fa/totp/enroll`, {
    challengeToken,
    otp,
  });
  return resp.data;
}

/**
 * Starts the enrollment of a Google challenge without an emailed proof (XBIT-4796 D8): the Google sign-in
 * replaced it. Answers the otpauth URI once, and only to the IP that made the Google callback request.
 */
export async function startTotpEnrollmentWithoutEmail(challengeToken: string): Promise<{ otpauthUri: string }> {
  const resp = await axios.post<{ otpauthUri: string }>(`${baseUrl}/auth/2fa/totp/enroll/start`, {
    challengeToken,
  });
  return resp.data;
}

export async function confirmTotpEnrollment(challengeToken: string, otp: string): Promise<AuthModel> {
  const resp = await axios.post<AuthModel>(`${baseUrl}/auth/2fa/totp/enroll/confirm`, {
    challengeToken,
    otp,
  });
  return resp.data;
}

/**
 * Exchanges a refresh token for a new access/refresh pair (the service rotates the refresh token).
 * The refresh token goes in the Authorization header; the data provider is not used so this call
 * never re-enters `getAuth()`.
 */
export async function refreshSession(refreshToken: string): Promise<AuthModel> {
  const url = `${baseUrl}/auth/refresh-token`;
  // `_skipAuthRetry` is read by the response interceptor (authInterceptor.ts); it is never sent over the wire.
  const resp = await axios.post<AuthModel>(url, undefined, {
    headers: { Authorization: `Bearer ${refreshToken}` },
    _skipAuthRetry: true,
  } as AxiosRequestConfig);
  return resp.data;
}

/**
 * Ends the server-side session of this access token (XBIT-4826). Goes through the default axios instance so
 * the interceptor's refresh-and-retry applies to a superseded or expired token.
 */
export async function logoutSession(accessToken: string): Promise<void> {
  await axios.post(`${baseUrl}/auth/logout`, undefined, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export interface StepUpAnswer {
  stepUpToken: string;
  expiresIn: number;
  expiresAt: string;
}

/**
 * Trades the signed-in admin's own authenticator code for a step-up grant (XBIT-4777). The route is guarded, so the
 * access token goes in the Authorization header like `logoutSession`. Through the default
 * axios instance so the 401 refresh still applies; `_skipStepUp` keeps a refusal here from opening the dialog
 * again (the interceptor reads it, it is never sent).
 */
export async function stepUp(otp: string, accessToken: string): Promise<StepUpAnswer> {
  const resp = await axios.post<StepUpAnswer>(`${baseUrl}/auth/step-up`, { otp }, {
    headers: { Authorization: `Bearer ${accessToken}` },
    _skipStepUp: true,
  } as AxiosRequestConfig);
  return resp.data;
}

export interface AuthProviders {
  password: boolean;
  google: boolean;
  /** Absent on a service older than XBIT-4742; present, it lists the methods that are usable now. */
  twoFactor?: { totp: boolean; emailOtp: boolean; totpRequired?: boolean };
}

/**
 * Which sign-in methods the service has enabled (XBIT-4719). Public route: no Authorization header.
 * Rejects on a non-2xx or a body whose `password` / `google` are not booleans. `twoFactor` is returned
 * only when both of its fields are booleans, else omitted (an older service, or a malformed field).
 * `twoFactor.totpRequired` (XBIT-4796) is kept only when it is a boolean, else omitted.
 */
export async function getAuthProviders(): Promise<AuthProviders> {
  const url = `${baseUrl}/auth/providers`;
  const resp = await axios.get<AuthProviders>(url);
  const { password, google, twoFactor } = resp.data ?? ({} as Partial<AuthProviders>);
  if (typeof password !== 'boolean' || typeof google !== 'boolean') {
    throw new Error('Malformed /auth/providers response');
  }
  if (
    twoFactor &&
    typeof twoFactor.totp === 'boolean' &&
    typeof twoFactor.emailOtp === 'boolean'
  ) {
    return {
      password,
      google,
      twoFactor: {
        totp: twoFactor.totp,
        emailOtp: twoFactor.emailOtp,
        ...(typeof twoFactor.totpRequired === 'boolean' ? { totpRequired: twoFactor.totpRequired } : {}),
      },
    };
  }
  return { password, google };
}

/** The service's `{code, message}` out of a failed /auth/2fa/* call, with a generic fallback. */
export function twoFactorError(err: unknown): { code?: string; message: string } {
  const data = (err as { response?: { data?: { code?: string; message?: unknown } } })?.response?.data;
  const message = typeof data?.message === 'string' ? data.message : undefined;
  return {
    code: typeof data?.code === 'string' ? data.code : undefined,
    message: message ?? 'Something went wrong. Please try again.',
  };
}
